import { taskLabel } from "@/lib/task-reference";
import { useEffect, useRef, useState } from "react";
import { Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";

import { useFailCarrierTask } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { failureReasonsForTask, type TaskFailureTarget } from "./task-failure";

const MAX_EVIDENCE_BYTES = 5 * 1024 * 1024;
const EVIDENCE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "video/mp4",
  "video/quicktime",
  "video/webm",
]);

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result ?? "");
      resolve(value.includes(",") ? value.split(",")[1] : value);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function TaskFailureDialog({
  task,
  open,
  onOpenChange,
}: {
  task: TaskFailureTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const failTask = useFailCarrierTask();
  const submitLock = useRef(false);
  const [selectedReasonCode, setSelectedReasonCode] = useState("");
  const [notes, setNotes] = useState("");
  const [evidence, setEvidence] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const reasons = task ? failureReasonsForTask(task.type) : [];

  useEffect(() => {
    if (!open) {
      setSelectedReasonCode("");
      setNotes("");
      setEvidence(null);
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!task || !selectedReasonCode || submitLock.current) return;
    const reasonCode = Number(selectedReasonCode);
    const selectedReason = reasons.find((reason) => reason.code === reasonCode);
    if (!selectedReason) return;

    submitLock.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const media = evidence
        ? [
            {
              data: await fileToBase64(evidence),
              type: evidence.type.startsWith("video/")
                ? ("video" as const)
                : ("photo" as const),
              mimeType: evidence.type || "application/octet-stream",
            },
          ]
        : undefined;
      await failTask.mutateAsync({
        taskId: task.id,
        reason: selectedReason.label,
        reasonCode,
        notes: notes.trim() || undefined,
        media,
      });
      toast.success(`${taskLabel(task)} marked failed.`);
      onOpenChange(false);
    } catch {
      setError("Task could not be failed. Please retry.");
      toast.error("Task could not be failed. Please retry.");
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={task ? `Fail ${taskLabel(task)}` : "Fail Task"}
      closeDisabled={submitting}
      className="max-w-lg max-lg:[&_button]:min-h-11 max-lg:[&_button]:min-w-11 max-lg:[&_input]:min-h-11 max-lg:[&_select]:min-h-11"
      presentation="responsive-sheet"
      responsiveSheetAt="tablet"
      footer={
        <div className="flex justify-end gap-2 max-sm:flex-col-reverse">
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="dispatch-task-failure-form"
            variant="destructive"
            disabled={submitting || !selectedReasonCode}
          >
            {submitting && <Loader2 className="animate-spin" />}
            Confirm failure
          </Button>
        </div>
      }
    >
      <form
        id="dispatch-task-failure-form"
        className="space-y-4"
        onSubmit={handleSubmit}
      >
        <p className="text-sm text-muted-foreground">
          Record the operational reason. Retry, cancellation, and final-failure
          behavior follow the shared Task lifecycle.
        </p>
        <div className="space-y-1.5">
          <label
            htmlFor="dispatch-failure-reason"
            className="text-sm font-medium"
          >
            Failure reason
          </label>
          <NativeSelect
            id="dispatch-failure-reason"
            aria-label="Failure reason"
            value={selectedReasonCode}
            onChange={(event) => {
              setSelectedReasonCode(event.target.value);
              setError(null);
            }}
          >
            <option value="">Select a reason</option>
            {reasons.map((reason) => (
              <option key={reason.code} value={String(reason.code)}>
                {reason.label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="dispatch-failure-notes"
            className="text-sm font-medium"
          >
            Failure notes
          </label>
          <Textarea
            id="dispatch-failure-notes"
            aria-label="Failure notes"
            rows={3}
            value={notes}
            placeholder="Optional operational details"
            onChange={(event) => {
              setNotes(event.target.value);
              setError(null);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="dispatch-failure-evidence"
            className="flex items-center gap-1.5 text-sm font-medium"
          >
            <Paperclip className="h-4 w-4" />
            Evidence (optional)
          </label>
          <Input
            id="dispatch-failure-evidence"
            aria-label="Failure evidence"
            type="file"
            accept={Array.from(EVIDENCE_MIME_TYPES).join(",")}
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              if (file && !EVIDENCE_MIME_TYPES.has(file.type)) {
                setEvidence(null);
                setError("Evidence must be a supported image or video file.");
              } else if (file && file.size > MAX_EVIDENCE_BYTES) {
                setEvidence(null);
                setError("Evidence must be 5 MB or smaller.");
              } else {
                setEvidence(file);
                setError(null);
              }
            }}
          />
        </div>
        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </div>
        )}
      </form>
    </Dialog>
  );
}
